import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	jobList: [],
	jobDetail: null,
	candidateList: [],
	candidateDetail: null,
	interviewList: [],
	offerList: [],
	loading: false,
	error: null,
};

// Job stages for pipeline (key used in UI; StageId sent to API)
export const CANDIDATE_STAGES = [
	{ key: "APPLIED", label: "Applied", color: "blue", stageId: 1 },
	{ key: "SCREENING", label: "Screening", color: "cyan", stageId: 2 },
	{ key: "INTERVIEW", label: "Interview", color: "purple", stageId: 3 },
	{ key: "OFFER", label: "Offer", color: "orange", stageId: 4 },
	{ key: "HIRED", label: "Hired", color: "green", stageId: 5 },
	{ key: "REJECTED", label: "Rejected", color: "red", stageId: 6 },
];

const stageKeyToId = (key) => {
	const s = CANDIDATE_STAGES.find((c) => String(c.key).toUpperCase() === String(key).toUpperCase());
	return s?.stageId ?? 1;
};
const stageIdToKey = (id) => {
	const s = CANDIDATE_STAGES.find((c) => c.stageId === Number(id));
	return s?.key ?? "APPLIED";
};

// —— Jobs —— (GET hiring/JobOpeningGetByCompanyId)
export const getJobList = createAsyncThunk(
	"hiring/getJobList",
	async (_params, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("hiring/JobOpeningGetByCompanyId");
			const list = data?.list ?? data?.data ?? (Array.isArray(data) ? data : []);
			return { list, total: data?.total ?? data?.Total ?? list.length };
		} catch (error) {
			if (error.response?.status !== 404) {
				toast.error(error.response?.data?.message || "Failed to load jobs");
			}
			return rejectWithValue(error.response?.data || {});
		}
	}
);

export const getJobById = createAsyncThunk(
	"hiring/getJobById",
	async (id, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`hiring/jobs/${id}`);
			return data?.data ?? data ?? null;
		} catch (error) {
			if (error.response?.status !== 404) {
				toast.error(error.response?.data?.message || "Failed to load job");
			}
			return rejectWithValue(error.response?.data || {});
		}
	}
);

// Map form status to API: "Open" | "Closed"
const mapStatusToApi = (s) => {
	if (s === "Closed" || s === "CLOSED") return "Closed";
	return "Open";
};

// Build payload for POST hiring/JobOpeningSetup — add: JobOpeningId 0, edit: pass JobOpeningId, delete: DataStatus 2
const buildJobOpeningPayload = (values, isEdit = false, isDelete = false) => ({
	JobOpeningId: isEdit ? (values.JobOpeningId ?? values.id ?? values.JobId) : 0,
	Title: values.Title ?? values.title ?? "",
	DepartmentId: values.DepartmentId ?? values.departmentId ?? null,
	DesignationId: values.DesignationId ?? values.designationId ?? null,
	HeadCount: values.HeadCount ?? values.headcount ?? 1,
	Status: mapStatusToApi(values.Status ?? values.status ?? "Open"),
	DataStatus: isDelete ? 2 : 1,
});

export const createJob = createAsyncThunk(
	"hiring/createJob",
	async (payload, { rejectWithValue }) => {
		try {
			const body = buildJobOpeningPayload(payload, false, false);
			const { data } = await axios.post("hiring/JobOpeningSetup", body);
			toast.success("Job created successfully");
			return data?.data ?? data ?? body;
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to create job";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

export const updateJob = createAsyncThunk(
	"hiring/updateJob",
	async ({ id, ...payload }, { rejectWithValue }) => {
		try {
			const body = buildJobOpeningPayload(
				{ ...payload, JobOpeningId: id ?? payload.JobOpeningId },
				true,
				false
			);
			const { data } = await axios.post("hiring/JobOpeningSetup", body);
			toast.success("Job updated successfully");
			return data?.data ?? data ?? { ...body, JobOpeningId: id };
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to update job";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

export const deleteJob = createAsyncThunk(
	"hiring/deleteJob",
	async (record, { rejectWithValue }) => {
		try {
			const id = record?.JobOpeningId ?? record?.id ?? record?.JobId;
			const body = buildJobOpeningPayload(
				{
					JobOpeningId: id,
					Title: record?.Title ?? record?.title ?? "",
					DepartmentId: record?.DepartmentId ?? record?.departmentId ?? null,
					DesignationId: record?.DesignationId ?? record?.designationId ?? null,
					HeadCount: record?.HeadCount ?? record?.headcount ?? 1,
					Status: record?.Status ?? record?.status ?? "Open",
				},
				true,
				true
			);
			await axios.post("hiring/JobOpeningSetup", body);
			toast.success("Job deleted successfully");
			return id;
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to delete job";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

// —— Candidates —— (GET hiring/CandidateGetByCompanyId, POST hiring/CandidateSetup)
// Build payload for POST hiring/CandidateSetup — add: CandidateId 0, edit: pass CandidateId, delete: DataStatus 2
const buildCandidateSetupPayload = (values, isEdit = false, isDelete = false) => ({
	CandidateId: isEdit ? (values.CandidateId ?? values.id ?? values.candidateId) : 0,
	TitleTypeId: values.TitleTypeId ?? values.titleTypeId ?? 1,
	FirstName: values.FirstName ?? values.firstName ?? "",
	MiddleName: values.MiddleName ?? values.middleName ?? "",
	LastName: values.LastName ?? values.lastName ?? "",
	Email: values.Email ?? values.email ?? "",
	Phone: values.Phone ?? values.phone ?? "",
	JobOpeningId: values.JobOpeningId ?? values.jobOpeningId ?? values.jobId ?? null,
	Source: values.Source ?? values.source ?? "",
	StageId: values.StageId ?? values.stageId ?? 1,
	Remarks: values.Remarks ?? values.remarks ?? null,
	DataStatus: isDelete ? 2 : 1,
});

export const getCandidateList = createAsyncThunk(
	"hiring/getCandidateList",
	async (_params, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("hiring/CandidateGetByCompanyId");
			const list = data?.list ?? data?.data ?? (Array.isArray(data) ? data : []);
			return { list, total: data?.total ?? data?.Total ?? list.length };
		} catch (error) {
			if (error.response?.status !== 404) {
				toast.error(error.response?.data?.message || "Failed to load candidates");
			}
			return rejectWithValue(error.response?.data || {});
		}
	}
);

export const getCandidateById = createAsyncThunk(
	"hiring/getCandidateById",
	async (id, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`hiring/candidates/${id}`);
			return data?.data ?? data ?? null;
		} catch (error) {
			if (error.response?.status !== 404) {
				toast.error(error.response?.data?.message || "Failed to load candidate");
			}
			return rejectWithValue(error.response?.data || {});
		}
	}
);

export const createCandidate = createAsyncThunk(
	"hiring/createCandidate",
	async (payload, { rejectWithValue }) => {
		try {
			const body = buildCandidateSetupPayload(
				{ ...payload, StageId: payload.StageId ?? stageKeyToId(payload.stage) ?? 1 },
				false,
				false
			);
			const { data } = await axios.post("hiring/CandidateSetup", body);
			toast.success("Candidate added successfully");
			return data?.data ?? data ?? body;
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to add candidate";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

export const updateCandidate = createAsyncThunk(
	"hiring/updateCandidate",
	async ({ id, ...payload }, { rejectWithValue }) => {
		try {
			const body = buildCandidateSetupPayload(
				{ ...payload, CandidateId: id ?? payload.CandidateId, StageId: payload.StageId ?? stageKeyToId(payload.stage) ?? 1 },
				true,
				false
			);
			const { data } = await axios.post("hiring/CandidateSetup", body);
			toast.success("Candidate updated successfully");
			return data?.data ?? data ?? { ...body, CandidateId: id };
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to update candidate";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

export const deleteCandidate = createAsyncThunk(
	"hiring/deleteCandidate",
	async (record, { rejectWithValue }) => {
		try {
			const body = buildCandidateSetupPayload(
				{
					CandidateId: record?.CandidateId ?? record?.id ?? record?.candidateId,
					TitleTypeId: record?.TitleTypeId ?? record?.titleTypeId ?? 1,
					FirstName: record?.FirstName ?? record?.firstName ?? "",
					MiddleName: record?.MiddleName ?? record?.middleName ?? "",
					LastName: record?.LastName ?? record?.lastName ?? "",
					Email: record?.Email ?? record?.email ?? "",
					Phone: record?.Phone ?? record?.phone ?? "",
					JobOpeningId: record?.JobOpeningId ?? record?.jobOpeningId ?? record?.jobId ?? null,
					Source: record?.Source ?? record?.source ?? "",
					StageId: record?.StageId ?? record?.stageId ?? 1,
					Remarks: record?.Remarks ?? record?.remarks ?? null,
				},
				true,
				true
			);
			await axios.post("hiring/CandidateSetup", body);
			toast.success("Candidate deleted successfully");
			return record?.CandidateId ?? record?.id ?? record?.candidateId;
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to delete candidate";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

export const updateCandidateStage = createAsyncThunk(
	"hiring/updateCandidateStage",
	async ({ id, stage, record }, { rejectWithValue }) => {
		try {
			const stageId = typeof stage === "number" ? stage : stageKeyToId(stage);
			const r = record ?? {};
			const body = buildCandidateSetupPayload(
				{
					CandidateId: id ?? r.CandidateId ?? r.id,
					TitleTypeId: r.TitleTypeId ?? r.titleTypeId ?? 1,
					FirstName: r.FirstName ?? r.firstName ?? "",
					MiddleName: r.MiddleName ?? r.middleName ?? "",
					LastName: r.LastName ?? r.lastName ?? "",
					Email: r.Email ?? r.email ?? "",
					Phone: r.Phone ?? r.phone ?? "",
					JobOpeningId: r.JobOpeningId ?? r.jobOpeningId ?? r.jobId ?? null,
					Source: r.Source ?? r.source ?? "",
					StageId: stageId,
					Remarks: r.Remarks ?? r.remarks ?? null,
				},
				true,
				false
			);
			const { data } = await axios.post("hiring/CandidateSetup", body);
			toast.success("Stage updated");
			return data?.data ?? data ?? { id, stage: stageIdToKey(stageId) };
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to update stage";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

export { stageIdToKey, stageKeyToId };

// —— Interviews ——
export const getInterviewsByCandidate = createAsyncThunk(
	"hiring/getInterviewsByCandidate",
	async (candidateId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`hiring/candidates/${candidateId}/interviews`);
			const list = data?.list ?? data?.data ?? (Array.isArray(data) ? data : []);
			return list;
		} catch (error) {
			if (error.response?.status !== 404) {
				toast.error(error.response?.data?.message || "Failed to load interviews");
			}
			return rejectWithValue(error.response?.data || {});
		}
	}
);

export const createInterview = createAsyncThunk(
	"hiring/createInterview",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await axios.post("hiring/interviews", payload);
			toast.success("Interview scheduled");
			return data?.data ?? data ?? null;
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to schedule interview";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

// —— Offers ——
export const getOfferList = createAsyncThunk(
	"hiring/getOfferList",
	async ({ page = 1, limit = 50, status } = {}, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams();
			if (page) params.append("page", page);
			if (limit) params.append("limit", limit);
			if (status) params.append("status", status);
			const { data } = await axios.get(
				`hiring/offers${params.toString() ? `?${params.toString()}` : ""}`
			);
			const list = data?.list ?? data?.data ?? (Array.isArray(data) ? data : []);
			return { list, total: data?.total ?? data?.Total ?? list.length };
		} catch (error) {
			if (error.response?.status !== 404) {
				toast.error(error.response?.data?.message || "Failed to load offers");
			}
			return rejectWithValue(error.response?.data || {});
		}
	}
);

export const createOffer = createAsyncThunk(
	"hiring/createOffer",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await axios.post("hiring/offers", payload);
			toast.success("Offer created successfully");
			return data?.data ?? data ?? null;
		} catch (error) {
			const msg = error.response?.data?.message || "Failed to create offer";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

const hiringSlice = createSlice({
	name: "hiring",
	initialState,
	reducers: {
		clearHiringError: (state) => {
			state.error = null;
		},
		clearJobDetail: (state) => {
			state.jobDetail = null;
		},
		clearCandidateDetail: (state) => {
			state.candidateDetail = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Jobs
			.addCase(getJobList.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getJobList.fulfilled, (state, action) => {
				state.loading = false;
				state.jobList = action.payload?.list ?? [];
			})
			.addCase(getJobList.rejected, (state) => {
				state.loading = false;
				state.jobList = [];
			})
			.addCase(getJobById.fulfilled, (state, action) => {
				state.jobDetail = action.payload ?? null;
			})
			.addCase(createJob.fulfilled, (state, action) => {
				if (action.payload && state.jobList) {
					state.jobList = [action.payload, ...state.jobList];
				}
			})
			.addCase(updateJob.fulfilled, (state, action) => {
				const updated = action.payload;
				const updatedId = updated?.id ?? updated?.JobId ?? updated?.JobOpeningId;
				if (updated && state.jobList?.length) {
					const idx = state.jobList.findIndex(
						(j) => (j.id ?? j.JobId ?? j.JobOpeningId) === updatedId
					);
					if (idx !== -1) state.jobList[idx] = { ...state.jobList[idx], ...updated };
				}
				if (updated && state.jobDetail && (state.jobDetail.id ?? state.jobDetail.JobId ?? state.jobDetail.JobOpeningId) === updatedId) {
					state.jobDetail = { ...state.jobDetail, ...updated };
				}
			})
			.addCase(deleteJob.fulfilled, (state, action) => {
				const deletedId = action.payload;
				if (deletedId != null && state.jobList?.length) {
					state.jobList = state.jobList.filter(
						(j) => (j.id ?? j.JobId ?? j.JobOpeningId) !== deletedId
					);
				}
				if (state.jobDetail && (state.jobDetail.id ?? state.jobDetail.JobId ?? state.jobDetail.JobOpeningId) === deletedId) {
					state.jobDetail = null;
				}
			})
			// Candidates
			.addCase(getCandidateList.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getCandidateList.fulfilled, (state, action) => {
				state.loading = false;
				state.candidateList = action.payload?.list ?? [];
			})
			.addCase(getCandidateList.rejected, (state) => {
				state.loading = false;
				state.candidateList = [];
			})
			.addCase(getCandidateById.fulfilled, (state, action) => {
				state.candidateDetail = action.payload ?? null;
			})
			.addCase(createCandidate.fulfilled, (state, action) => {
				if (action.payload && state.candidateList) {
					state.candidateList = [action.payload, ...state.candidateList];
				}
			})
			.addCase(updateCandidate.fulfilled, (state, action) => {
				const updated = action.payload;
				const updatedId = updated?.CandidateId ?? updated?.id ?? updated?.candidateId;
				if (updated && state.candidateList?.length) {
					const idx = state.candidateList.findIndex(
						(c) => (c.CandidateId ?? c.id ?? c.candidateId) === updatedId
					);
					if (idx !== -1) state.candidateList[idx] = { ...state.candidateList[idx], ...updated };
				}
			})
			.addCase(deleteCandidate.fulfilled, (state, action) => {
				const deletedId = action.payload;
				if (deletedId != null && state.candidateList?.length) {
					state.candidateList = state.candidateList.filter(
						(c) => (c.CandidateId ?? c.id ?? c.candidateId) !== deletedId
					);
				}
			})
			.addCase(updateCandidateStage.fulfilled, (state, action) => {
				const updated = action.payload;
				const id = updated?.CandidateId ?? updated?.id;
				if (id && state.candidateList?.length) {
					const idx = state.candidateList.findIndex(
						(c) => (c.CandidateId ?? c.id ?? c.candidateId) === id
					);
					if (idx !== -1) state.candidateList[idx] = { ...state.candidateList[idx], ...updated, StageId: updated?.StageId ?? state.candidateList[idx].StageId };
				}
			})
			// Interviews
			.addCase(getInterviewsByCandidate.fulfilled, (state, action) => {
				state.interviewList = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(getInterviewsByCandidate.rejected, (state) => {
				state.interviewList = [];
			})
			// Offers
			.addCase(getOfferList.pending, (state) => {
				state.loading = true;
			})
			.addCase(getOfferList.fulfilled, (state, action) => {
				state.loading = false;
				state.offerList = action.payload?.list ?? [];
			})
			.addCase(getOfferList.rejected, (state) => {
				state.loading = false;
				state.offerList = [];
			})
			.addCase(createOffer.fulfilled, (state, action) => {
				if (action.payload && state.offerList) {
					state.offerList = [action.payload, ...state.offerList];
				}
			});
	},
});

export const { clearHiringError, clearJobDetail, clearCandidateDetail } = hiringSlice.actions;
export default hiringSlice.reducer;
